import { Col, Row } from 'antd';
import { formatToDate } from 'utils/formatToDate';

import { CERTIFICATE_REVIEW_STATUS, DocumentDetailsModel } from 'api/documents/documents.model';
import { BasicCard } from 'components/BasicCard';
import CardRow from 'components/CardRow';
import CompositionRow from 'components/CompositionRow';
import Detail from 'components/Detail';
import SectionTitle from 'components/SectionTitle';
import SubTitle from 'components/SubTitle';
import useCertificateHelper from 'hooks/useCertificateHelper';
import {
  ADDITIONAL_DETAILS,
  CertificateDetailsModel,
  NESTED_DETAILS,
} from 'models/certificate.model';

import RelatedCertificates from '../components/RelatedCertificates';
import ReviewStatus from '../components/ReviewStatus';
import Template from '../components/Template';

const BatteryTechReview = ({
  additionalDetails = {} as CertificateDetailsModel,
  ...document
}: DocumentDetailsModel<CertificateDetailsModel>) => {
  const { getLabel, getValueByLabel } = useCertificateHelper();

  return (
    <Template
      type={document.category}
      jobId={document.jobId}
      favourite={document.favourite}
      batchCard={
        <>
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_PACK_SERIAL_NUMBER)}
            value={getValueByLabel(
              NESTED_DETAILS.BATTERY_PACK_SERIAL_NUMBER,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail label={getLabel('passportId')} value={document.documentBlockchainAddress} />
          <CardRow>
            <Detail
              label={getLabel(ADDITIONAL_DETAILS.ASSEMBLY_NUMBER)}
              value={additionalDetails[ADDITIONAL_DETAILS.ASSEMBLY_NUMBER]}
            />
            <Detail
              label={getLabel(ADDITIONAL_DETAILS.CONTROL_NUMBER)}
              value={additionalDetails[ADDITIONAL_DETAILS.CONTROL_NUMBER]}
            />
          </CardRow>
        </>
      }
      detailsCard={
        <CardRow>
          <Detail
            label={getLabel(NESTED_DETAILS.EVMODEL)}
            value={getValueByLabel(
              NESTED_DETAILS.EVMODEL,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_PRODUCERS)}
            value={getValueByLabel(
              NESTED_DETAILS.BATTERY_PRODUCERS,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_CELL_MODEL)}
            value={getValueByLabel(
              NESTED_DETAILS.BATTERY_CELL_MODEL,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_PRODUCTION_DATE)}
            value={formatToDate(
              getValueByLabel(
                NESTED_DETAILS.BATTERY_PRODUCTION_DATE,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )
            )}
          />
          <Detail
            label={getLabel('baterryPackTechnicalReviewCertificateUID')}
            value={additionalDetails.productId}
          />
        </CardRow>
      }
    >
      <Row gutter={[16, 16]}>
        <Col md={24} lg={10}>
          <SectionTitle>{getLabel('technicalReviewInformation')}</SectionTitle>

          <ReviewStatus
            status={
              getValueByLabel(
                NESTED_DETAILS.TECHNICAL_REVIEW_STATUS,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              ) as CERTIFICATE_REVIEW_STATUS
            }
          />
          <br />
          <SubTitle>{getLabel('dataInfo')}</SubTitle>
          <BasicCard>
            <CardRow>
              <Detail
                label={getLabel(NESTED_DETAILS.SALE_DATE)}
                value={formatToDate(
                  getValueByLabel(
                    NESTED_DETAILS.SALE_DATE,
                    additionalDetails[ADDITIONAL_DETAILS.DETAILS]
                  )
                )}
              />
              <Detail
                label={getLabel(NESTED_DETAILS.BATTERY_PRODUCTION_DATE)}
                value={formatToDate(
                  getValueByLabel(
                    NESTED_DETAILS.BATTERY_PRODUCTION_DATE,
                    additionalDetails[ADDITIONAL_DETAILS.DETAILS]
                  )
                )}
              />
              <Detail
                label={getLabel(NESTED_DETAILS.TECHNICAL_REVIEW_DATE)}
                value={formatToDate(
                  getValueByLabel(
                    NESTED_DETAILS.TECHNICAL_REVIEW_DATE,
                    additionalDetails[ADDITIONAL_DETAILS.DETAILS]
                  )
                )}
              />
            </CardRow>
          </BasicCard>
        </Col>
        <Col md={24} lg={14}>
          <RelatedCertificates
            additionalDetails={additionalDetails}
            relatedFields={['batteryPackSerialNumber']}
          />
        </Col>
      </Row>

      <SectionTitle>{getLabel('specification')}</SectionTitle>
      <SubTitle>{getLabel('generalInfo')}</SubTitle>
      <CompositionRow<NESTED_DETAILS>
        composition={additionalDetails?.[ADDITIONAL_DETAILS.DETAILS]}
        labels={[
          NESTED_DETAILS.WEIGHT,
          NESTED_DETAILS.GRAVIMETRIC_ENERGY_DENSITY,
          NESTED_DETAILS.COOLING,
          NESTED_DETAILS.STORAGE_TEMP_RANGE,
          NESTED_DETAILS.CELLS_SETUP,
          NESTED_DETAILS.TOTAL_CAPACITY,
          NESTED_DETAILS.USABLE_CAPACITY,
        ]}
      />

      <SubTitle>{getLabel(NESTED_DETAILS.DESCRIPTION)}</SubTitle>
      <BasicCard>
        {getValueByLabel(NESTED_DETAILS.DESCRIPTION, additionalDetails[ADDITIONAL_DETAILS.DETAILS])}
      </BasicCard>
    </Template>
  );
};

export default BatteryTechReview;
